/**
 * Partner Dashboard
 *
 * Main dashboard for partner administrators showing:
 * - Partner profile overview
 * - Organization management
 * - User overview
 * - Analytics summary
 *
 * IMPORTANT: Partners cannot access clinical data (PHI) - HIPAA compliance
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Building2,
  Users,
  TrendingUp,
  Search,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Building
} from 'lucide-react';
import { apiService } from '@/services/api';
import { toast } from 'react-hot-toast';
import { DataTable, ColumnDefinition, SortState } from '@/components/shared/DataTable';

interface PartnerProfile {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  isOwnerPartner: boolean;
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone?: string;
  };
  quotas: {
    maxOrganizations: number;
    maxUsersPerOrg: number;
    maxStorageGB: number;
  };
  features: {
    whiteLabeling: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    advancedAnalytics: boolean;
  };
  stats: {
    organizationCount: number;
    userCount: number;
    quotaUsed: {
      organizations: number;
      maxOrganizations: number;
    };
  };
}

interface Organization {
  id: string;
  name: string;
  subdomain: string;
  type: string;
  subscription: {
    status: string;
    /** The plan's tier. Null when the subscription's plan is not resolved. */
    plan: string | null;
  };
  isActive: boolean;
  userCount: number;
  createdAt: string;
}

interface PartnerAnalytics {
  overview: {
    totalOrganizations: number;
    activeOrganizations: number;
    totalUsers: number;
    totalEncounters: number;
  };
  recentActivity: {
    newOrganizationsLast30Days: number;
    newUsersLast30Days: number;
  };
  organizationsByStatus: Record<string, number>;
}

const PartnerOrganizations: React.FC = () => {
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [analytics, setAnalytics] = useState<PartnerAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortState, setSortState] = useState<SortState>({ column: 'name', direction: 'asc' });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Define response types matching backend API structure
      interface ProfileResponse { success: boolean; data: PartnerProfile }
      interface OrgsResponse { success: boolean; data: { organizations: Organization[] } }
      interface AnalyticsResponse { success: boolean; data: PartnerAnalytics }

      const [profileRes, orgsRes, analyticsRes] = await Promise.all([
        apiService.get<ProfileResponse>('/v1/partner/profile'),
        apiService.get<OrgsResponse>('/v1/partner/organizations'),
        apiService.get<AnalyticsResponse>('/v1/partner/analytics')
      ]);

      setProfile(profileRes.data);
      setOrganizations(orgsRes.data?.organizations || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to load partner data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  // DataTable column definitions
  const organizationColumns: ColumnDefinition<Organization>[] = useMemo(() => [
    {
      id: 'organization',
      header: 'Organization',
      sortable: true,
      sortKey: 'name',
      accessor: (org) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{org.name}</div>
          <div className="text-xs text-gray-500">{org.subdomain}</div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      sortable: true,
      sortKey: 'type',
      accessor: (org) => (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 capitalize">
          {org.type}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'subscription.status',
      accessor: (org) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(org.subscription?.status)}
          <span className="text-sm text-gray-700 capitalize">
            {org.subscription?.status || 'No subscription'}
          </span>
        </div>
      ),
    },
    {
      id: 'plan',
      header: 'Plan',
      sortable: true,
      sortKey: 'subscription.plan',
      accessor: (org) => (
        <span className="text-sm text-gray-700 capitalize">
          {org.subscription?.plan || '-'}
        </span>
      ),
    },
    {
      id: 'users',
      header: 'Users',
      sortable: true,
      sortKey: 'userCount',
      accessor: (org) => (
        <span className="text-sm text-gray-700">{org.userCount}</span>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      sortable: true,
      sortKey: 'createdAt',
      accessor: (org) => (
        <span className="text-sm text-gray-500">
          {new Date(org.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ], []);

  // No row actions: view and edit pointed at /partner/organizations/:id and /:id/edit, which
  // have no routes, and delete plus create are requirePartnerAdmin endpoints that no role
  // reaching this portal holds. Read-only detail pages are tracked as a follow-up.

  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.subdomain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sorted data
  const sortedOrgs = useMemo(() => {
    if (!sortState.column || !sortState.direction) return filteredOrgs;

    const getValue = (obj: any, path: string) =>
      path.split('.').reduce((acc, part) => acc?.[part], obj);

    return [...filteredOrgs].sort((a, b) => {
      const aVal = getValue(a, sortState.column!);
      const bVal = getValue(b, sortState.column!);
      if (aVal == null) return sortState.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortState.direction === 'asc' ? -1 : 1;
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : Number(aVal) - Number(bVal);
      return sortState.direction === 'asc' ? cmp : -cmp;
    });
  }, [filteredOrgs, sortState]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {profile?.name || 'Partner Portal'}
              </h1>
              {/* Read-only portal: promise nothing the roles that reach it cannot do. */}
              <p className="text-sm text-gray-500 mt-1">
                Organizations under your partner account
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                profile?.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {profile?.status?.toUpperCase()}
              </span>
              {profile?.isOwnerPartner && (
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-violet-100 text-violet-800">
                  OWNER
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Organizations</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {analytics?.overview.totalOrganizations || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-violet-100 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-violet-600" />
              </div>
            </div>
            {profile?.quotas.maxOrganizations !== -1 && (
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Quota Used</span>
                  <span>{profile?.stats.quotaUsed.organizations}/{profile?.quotas.maxOrganizations}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-violet-600 h-2 rounded-full"
                    style={{
                      width: `${(profile?.stats.quotaUsed.organizations || 0) / (profile?.quotas.maxOrganizations || 1) * 100}%`
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Organizations</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {analytics?.overview.activeOrganizations || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {analytics?.overview.totalUsers || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">New This Month</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  +{analytics?.recentActivity.newOrganizationsLast30Days || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Organizations Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Organizations</h2>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          <DataTable
            data={sortedOrgs}
            columns={organizationColumns}
            striped={true}
            hoverable={true}
            sortable={true}
            sortState={sortState}
            onSortChange={setSortState}
            getRowId={(org) => org.id}
            ariaLabel="Partner organizations table"
            emptyMessage="No organizations found"
            emptyIcon={<Building className="h-12 w-12 text-gray-300" />}
          />
        </div>

        {/* HIPAA Notice */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-amber-800">HIPAA Compliance Notice</h3>
              <p className="text-sm text-amber-700 mt-1">
                As a partner administrator, you can manage organizations and users but cannot access
                clinical data or Protected Health Information (PHI). All clinical operations must be
                performed by organization-level users with appropriate clinical permissions.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PartnerOrganizations;
